// CMP-0102 · LFS Header (mega menus, search, mobile drawer) · Catalog Engine · Lot Discovery · owner: Platform
// 99-1: /Users/yogeshmathur/ym/lot-for-sale-ph/docs/design-handoff/README.md
// 99-1-decision: ADAPT the approved Concept D "Header D" prototype into one shared React header on the Spire Next.js app (registry SEL-LFS-001); counts come from the live Spire feed
"use client"

import Link from "next/link"
import { useEffect, useMemo, useState, type ReactNode } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Icon, Logo } from "./primitives"
import { INQUIRE, ROUTES, lotsHref } from "@/lib/lfs/catalog"
import type { NavData } from "@/lib/lfs/nav"
import { matchPlaces } from "@/lib/lfs/places"
// 99-1: /Users/yogeshmathur/ym/lot-for-sale-ph/docs/design-handoff/README.md
// 99-1-decision: shared place matcher

type Menu = "buy" | "loc" | "vil" | "pro" | "ins" | "search" | null

const MENUS: [Exclude<Menu, null | "search">, string][] = [
  ["buy", "Buy Lots"],
  ["loc", "Locations"],
  ["vil", "Villages & Subdivisions"],
  ["pro", "Projects"],
  ["ins", "Guides"],
]

const kicker = { margin: "0 0 12px", fontSize: 12, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: "#5E6776" } as const
const rowLink = { padding: "9px 10px", borderRadius: 8, fontSize: 15, color: "#121722", textDecoration: "none" } as const
const iconBtn = { width: 44, height: 44, padding: 0, border: "1px solid #d9e2ea", borderRadius: 10, background: "#fff", color: "#121722", cursor: "pointer" } as const
const count = (n: number, show: boolean) => (show ? n.toLocaleString("en-PH") : "")

// 99-1: /Users/yogeshmathur/ym/lot-for-sale-ph/docs/design-handoff/README.md
// 99-1-decision: header search uses the same full live place list and nickname matching as the hero (lib/lfs/places.ts)
function useSearch(nav: NavData, q: string) {
  return useMemo(
    () => matchPlaces(nav.places, q).map((p) => ({ label: p.label, kind: p.sub ? `${p.kind} · ${p.sub}` : p.kind, meta: `${p.count} lots`, href: p.href, icon: p.icon })),
    [nav, q],
  )
}

function Panel({ children }: { children: ReactNode }) {
  return (
    <div style={{ position: "absolute", left: 0, right: 0, top: 68, background: "#fff", borderBottom: "1px solid #dfe7ee", boxShadow: "0 24px 48px -28px rgba(16,40,63,.35)" }}>
      {children}
    </div>
  )
}

type DrawerItem = { label: string; href: string; icon: string; bg: string; fg: string; meta: string; onClick?: (e: React.MouseEvent) => void }

export function LfsHeader({ nav }: { nav: NavData }) {
  const [open, setOpen] = useState<Menu>(null)
  const [drawer, setDrawer] = useState(false)
  const [acc, setAcc] = useState<string | null>("vil")
  const [prov, setProv] = useState<string | null>(null)
  const [sq, setSq] = useState("")
  const [dq, setDq] = useState("")
  const sRes = useSearch(nav, sq)
  const dRes = useSearch(nav, dq)
  const show = nav.available

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(null)
        setDrawer(false)
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [])

  useEffect(() => {
    document.body.style.overflow = drawer ? "hidden" : ""
    return () => {
      document.body.style.overflow = ""
    }
  }, [drawer])

  const P = nav.provinces.find((p) => p.slug === prov)
  const closeAll = () => {
    setOpen(null)
    setDrawer(false)
    setDq("")
  }
  const openDrawer = () => {
    setProv(null)
    setDrawer(true)
  }

  const drawerGroups: { id: string; label: string; items: DrawerItem[] }[] = [
    {
      id: "buy",
      label: "Buy Lots",
      items: [
        { label: "All Lots for Sale", href: ROUTES.lots, icon: "landscape", bg: "#e9eff5", fg: "#121722", meta: count(nav.total, show) },
        ...nav.types.map((t) => ({ label: `${t.label} Lots`, href: t.href, icon: t.icon, bg: t.bg, fg: t.fg, meta: count(t.count, show) })),
      ],
    },
    {
      id: "loc",
      label: "Locations",
      items: [
        ...nav.provinces.map((p) => ({
          label: p.name, href: p.href, icon: "location_on", bg: "#e9eff5", fg: "#121722", meta: count(p.count, show),
          onClick: (e: React.MouseEvent) => {
            e.preventDefault()
            setProv(p.slug)
          },
        })),
        { label: "Browse all locations", href: ROUTES.locations, icon: "travel_explore", bg: "#eef3f7", fg: "#121722", meta: "" },
      ],
    },
    {
      id: "vil",
      label: "Villages & Subdivisions",
      items: [
        ...nav.villages.map((v) => ({ label: v.name, href: v.href, icon: "shield_person", bg: "#e9f0f9", fg: "#245fa3", meta: String(v.count) })),
        { label: "All villages & subdivisions", href: ROUTES.villages, icon: "arrow_forward", bg: "#eef3f7", fg: "#121722", meta: "" },
      ],
    },
    {
      id: "pro",
      label: "Projects",
      items: [
        ...nav.projects.map((p) => ({ label: p.name, href: p.href, icon: "holiday_village", bg: "#e9eff5", fg: "#121722", meta: String(p.count) })),
        { label: "All land developments", href: ROUTES.developments, icon: "arrow_forward", bg: "#eef3f7", fg: "#121722", meta: "" },
      ],
    },
    {
      id: "ins",
      label: "Property Guides",
      items: [
        ...nav.guides.slice(0, 5).map((g) => ({ label: g.title, href: g.href, icon: "article", bg: "#EAF0FE", fg: "#1A5CF0", meta: "" })),
        { label: "All guides", href: ROUTES.insights, icon: "arrow_forward", bg: "#eef3f7", fg: "#121722", meta: "" },
      ],
    },
  ]

  return (
    <header
      style={{ position: "sticky", top: 0, zIndex: 50, background: "#fff", borderBottom: "1px solid #dfe7ee", color: "#121722" }}
      onMouseLeave={() => open && open !== "search" && setOpen(null)}
    >
      <div style={{ padding: "0 var(--lfs-gutter)", height: 68, display: "flex", alignItems: "center", gap: "clamp(8px,1.6vw,24px)" }}>
        <Logo />

        {/* desktop (>= 1240px) */}
        <nav aria-label="Main" className="lfs-desk" style={{ alignItems: "center", flex: 1, minWidth: 0 }}>
          {MENUS.map(([id, label]) => (
            <Button
              key={id}
              variant="ghost"
              type="button"
              aria-expanded={open === id}
              aria-haspopup="true"
              onClick={() => setOpen(open === id ? null : id)}
              onMouseEnter={() => open && open !== "search" && setOpen(id)}
              style={{ height: 40, padding: "0 9px 0 11px", border: 0, borderRadius: 8, background: open === id ? "#e9eff5" : "transparent", color: "#121722", font: "inherit", fontSize: 15, fontWeight: 500, gap: 1, cursor: "pointer" }}
            >
              {label}
              <Icon name={open === id ? "expand_less" : "expand_more"} size={20} color="#5E6776" />
            </Button>
          ))}
          <a href={INQUIRE.general} className="lfs-hover-bg" style={{ height: 40, padding: "0 11px", borderRadius: 8, display: "flex", alignItems: "center", textDecoration: "none", fontSize: 15, fontWeight: 500, whiteSpace: "nowrap" }}>
            Contact
          </a>
        </nav>
        <div className="lfs-desk" style={{ alignItems: "center", gap: 8, flexShrink: 0 }}>
          <Button variant="ghost" type="button" aria-label="Search lots" aria-expanded={open === "search"} onClick={() => setOpen(open === "search" ? null : "search")} style={iconBtn}>
            <Icon name="search" />
          </Button>
          <a href={INQUIRE.sell} className="lfs-btn-accent" style={{ height: 44, padding: "0 14px", borderRadius: 10, textDecoration: "none", display: "flex", alignItems: "center", gap: 7, fontSize: 15, fontWeight: 600, whiteSpace: "nowrap" }}>
            <Icon name="add_home" size={20} />
            List Your Lot
          </a>
        </div>

        {/* phone and tablet (< 1240px) */}
        <div className="lfs-mob" style={{ flex: 1, minWidth: 0, alignItems: "center", justifyContent: "flex-end", gap: 8 }}>
          <a href={INQUIRE.sell} className="lfs-btn-accent" style={{ height: 40, padding: "0 12px", borderRadius: 10, textDecoration: "none", display: "flex", alignItems: "center", gap: 5, fontSize: 14, fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}>
            <Icon name="add_home" size={19} />
            <span className="lfs-pill-short">List</span>
            <span className="lfs-pill-long">List Your Lot</span>
          </a>
          <Button variant="ghost" type="button" aria-label="Search lots" className="lfs-mob-search" onClick={openDrawer} style={{ ...iconBtn, border: 0, background: "transparent" }}>
            <Icon name="search" size={24} />
          </Button>
          <Button variant="ghost" type="button" aria-label="Open menu" aria-expanded={drawer} onClick={openDrawer} style={iconBtn}>
            <Icon name="menu" size={24} />
          </Button>
        </div>
      </div>

      {open === "buy" && (
        <Panel>
          <div style={{ padding: "28px var(--lfs-gutter) 32px", display: "grid", gridTemplateColumns: "minmax(0,1.6fr) minmax(0,1fr) minmax(0,1fr)", gap: 40 }}>
            <div>
              <p style={kicker}>By property type</p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
                <Link href={ROUTES.lots} className="lfs-hover-bg" style={{ display: "flex", gap: 12, padding: 10, borderRadius: 10, textDecoration: "none" }}>
                  <span style={{ width: 40, height: 40, borderRadius: 10, background: "#e9eff5", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="landscape" /></span>
                  <span><span style={{ display: "block", fontSize: 15, fontWeight: 600 }}>All Lots for Sale</span>{show && <span style={{ display: "block", fontSize: 13, color: "#5E6776", marginTop: 2 }}>{count(nav.total, show)} active listings</span>}</span>
                </Link>
                {nav.types.map((t) => (
                  <Link key={t.id} href={t.href} className="lfs-hover-bg" style={{ display: "flex", gap: 12, padding: 10, borderRadius: 10, textDecoration: "none" }}>
                    <span style={{ width: 40, height: 40, borderRadius: 10, background: t.bg, display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name={t.icon} color={t.fg} /></span>
                    <span><span style={{ display: "block", fontSize: 15, fontWeight: 600 }}>{t.label} Lots for Sale</span>{show && <span style={{ display: "block", fontSize: 13, color: "#5E6776", marginTop: 2 }}>{count(t.count, show)} listings</span>}</span>
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <p style={kicker}>By budget</p>
              <div style={{ display: "flex", flexDirection: "column" }}>
                {nav.budgets.map((b) => <Link key={b.label} href={b.href} className="lfs-hover-bg" style={rowLink}>{b.label}</Link>)}
              </div>
            </div>
            <div>
              <p style={kicker}>Popular searches</p>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <Link href={lotsHref({ type: "residential", province: "cavite" })} className="lfs-hover-bg" style={rowLink}>Residential lots in Cavite</Link>
                <Link href={lotsHref({ type: "commercial", province: "metro-manila", city: "makati" })} className="lfs-hover-bg" style={rowLink}>Commercial lots in Makati</Link>
                <Link href={lotsHref({ type: "industrial", province: "laguna" })} className="lfs-hover-bg" style={rowLink}>Industrial land in Laguna</Link>
                <Link href={lotsHref({ type: "agricultural", province: "batangas" })} className="lfs-hover-bg" style={rowLink}>Agricultural land in Batangas</Link>
                <Link href={lotsHref({ max: 5000000 })} className="lfs-hover-bg" style={rowLink}>Lots under ₱5M</Link>
              </div>
            </div>
          </div>
        </Panel>
      )}

      {open === "loc" && (
        <Panel>
          <div style={{ padding: "28px var(--lfs-gutter)", display: "grid", gridTemplateColumns: "minmax(0,1.5fr) minmax(0,1fr) minmax(0,.9fr)", gap: 40 }}>
            <div>
              <p style={kicker}>Popular cities</p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px 16px" }}>
                {nav.cities.slice(0, 10).map((c) => (
                  <Link key={c.name} href={c.href} className="lfs-hover-bg" style={{ ...rowLink, display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <span>{c.name}</span><span style={{ color: "#5E6776", fontVariantNumeric: "tabular-nums" }}>{c.count}</span>
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <p style={kicker}>Provinces and regions</p>
              <div style={{ display: "flex", flexDirection: "column" }}>
                {nav.provinces.slice(0, 10).map((p) => (
                  <Link key={p.slug} href={p.href} className="lfs-hover-bg" style={{ ...rowLink, padding: "8px 10px", display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <span>{p.name} <span style={{ fontSize: 13, color: "#5E6776" }}>· {p.region}</span></span><span style={{ color: "#5E6776", fontVariantNumeric: "tabular-nums" }}>{p.count}</span>
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <p style={kicker}>Discover more</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <Link href={ROUTES.locations} style={{ display: "flex", alignItems: "center", gap: 10, padding: 10, borderRadius: 10, fontSize: 15, fontWeight: 600, textDecoration: "none", background: "#eef3f7" }}><Icon name="travel_explore" />Browse all locations</Link>
                <Link href={`${ROUTES.locations}#search`} className="lfs-hover-bg" style={{ display: "flex", alignItems: "center", gap: 10, padding: 10, borderRadius: 10, fontSize: 15, textDecoration: "none" }}><Icon name="location_city" />Search by city or municipality</Link>
                <Link href={ROUTES.villages} className="lfs-hover-bg" style={{ display: "flex", alignItems: "center", gap: 10, padding: 10, borderRadius: 10, fontSize: 15, textDecoration: "none" }}><Icon name="sort_by_alpha" />Locations A–Z</Link>
              </div>
              <p style={{ margin: "14px 0 0", fontSize: 13, lineHeight: 1.45, color: "#5E6776" }}>Only places with active listings appear here.</p>
            </div>
          </div>
        </Panel>
      )}

      {open === "vil" && (
        <Panel>
          <div style={{ padding: "28px var(--lfs-gutter) 24px" }}>
            <p style={{ ...kicker, margin: "0 0 14px" }}>Villages and subdivisions with lots for sale</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 10 }}>
              {nav.villages.map((v) => (
                <Link key={v.name} href={v.href} className="lfs-hover-ink" style={{ display: "flex", flexDirection: "column", gap: 4, padding: 14, border: "1px solid #e3eaf1", borderRadius: 12, textDecoration: "none" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 16, fontWeight: 700 }}><Icon name="shield_person" size={20} color="#245fa3" />{v.name}</span>
                  <span style={{ fontSize: 13, color: "#4A5262" }}>{[v.city, v.province].filter(Boolean).join(", ")}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: "tabular-nums", marginTop: 4 }}>{v.count} lots · from {v.from}</span>
                </Link>
              ))}
            </div>
            <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid #e3eaf1", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <span style={{ fontSize: 13, color: "#5E6776" }}>Community names come from listing records; a barangay of the same name is not the same as the village.</span>
              <Link href={ROUTES.villages} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 15, fontWeight: 600, textDecoration: "none" }}>Browse all villages and subdivisions<Icon name="arrow_forward" size={18} /></Link>
            </div>
          </div>
        </Panel>
      )}

      {open === "pro" && (
        <Panel>
          <div style={{ padding: "28px var(--lfs-gutter) 24px", display: "grid", gridTemplateColumns: "minmax(0,3fr) minmax(0,1fr)", gap: 40 }}>
            <div>
              <p style={{ ...kicker, margin: "0 0 14px" }}>Land developments and estates</p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: "4px 16px" }}>
                {nav.projects.map((p) => (
                  <Link key={p.name} href={p.href} className="lfs-hover-bg" style={{ display: "flex", alignItems: "center", gap: 12, padding: 10, borderRadius: 10, textDecoration: "none" }}>
                    <span style={{ width: 40, height: 40, borderRadius: 10, background: "#e9eff5", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="holiday_village" /></span>
                    <span style={{ minWidth: 0 }}><span style={{ display: "block", fontSize: 15, fontWeight: 600 }}>{p.name}</span><span style={{ display: "block", fontSize: 13, color: "#5E6776", marginTop: 2 }}>{p.city} · {p.count} lots</span></span>
                  </Link>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <p style={{ ...kicker, margin: "0 0 10px" }}>Browse</p>
              <Link href={ROUTES.developments} className="lfs-hover-bg" style={{ display: "flex", alignItems: "center", gap: 10, padding: 10, borderRadius: 10, fontSize: 15, textDecoration: "none" }}><Icon name="apps" />Browse all land developments</Link>
            </div>
          </div>
        </Panel>
      )}

      {open === "ins" && (
        <Panel>
          <div style={{ padding: "28px var(--lfs-gutter)", display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: "4px 24px" }}>
            {nav.guides.map((g) => (
              <Link key={g.href} href={g.href} className="lfs-hover-bg" style={{ display: "flex", gap: 12, padding: 10, borderRadius: 10, textDecoration: "none" }}>
                <Icon name="article" color="#1A5CF0" style={{ marginTop: 1 }} />
                <span><span style={{ display: "block", fontSize: 15, fontWeight: 600, lineHeight: 1.3 }}>{g.title}</span><span style={{ display: "block", fontSize: 13, color: "#5E6776", marginTop: 3 }}>{g.topic} · {g.mins} min read</span></span>
              </Link>
            ))}
            <Link href={ROUTES.insights} style={{ display: "flex", alignItems: "center", gap: 6, padding: 10, fontSize: 15, fontWeight: 600, textDecoration: "none" }}>All Property Guides<Icon name="arrow_forward" size={18} /></Link>
            <Link href={ROUTES.about} style={{ display: "flex", alignItems: "center", gap: 6, padding: 10, fontSize: 15, textDecoration: "none" }}>About LOT FOR SALE<Icon name="arrow_forward" size={18} /></Link>
          </div>
        </Panel>
      )}

      {open === "search" && (
        <Panel>
          <form action={ROUTES.lots} style={{ maxWidth: 820, margin: "0 auto", padding: "24px var(--lfs-gutter) 28px" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 10, height: 56, padding: "0 6px 0 16px", border: "2px solid #121722", borderRadius: 12 }}>
              <Icon name="search" size={24} />
              <Input name="q" autoFocus value={sq} onChange={(e) => setSq(e.target.value)} autoComplete="off" placeholder="City, village, subdivision or project" aria-label="Search lots" style={{ flex: 1, height: 44, border: 0, boxShadow: "none", outline: "none", font: "inherit", fontSize: 17, background: "transparent", minWidth: 0, padding: 0 }} />
              <Button type="submit" className="lfs-btn-accent" style={{ height: 42, padding: "0 16px", border: 0, borderRadius: 8, font: "inherit", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>Search</Button>
            </label>
            {sRes.length > 0 ? (
              <div style={{ marginTop: 10, display: "flex", flexDirection: "column" }}>
                {sRes.map((s) => (
                  <Link key={s.kind + s.label} href={s.href} className="lfs-hover-bg" style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 8px", borderBottom: "1px solid #e9eff5", textDecoration: "none" }}>
                    <Icon name={s.icon} size={20} color="#5E6776" />
                    <span style={{ flex: 1 }}><span style={{ fontSize: 15, fontWeight: 600 }}>{s.label}</span> <span style={{ fontSize: 13, color: "#5E6776" }}>{s.kind}</span></span>
                    <span style={{ fontSize: 13, color: "#5E6776" }}>{s.meta}</span>
                  </Link>
                ))}
              </div>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 14 }}>
                {[...nav.villages.slice(0, 3), ...nav.cities.slice(0, 4)].map((p) => (
                  <Link key={p.name} href={p.href} style={{ height: 34, padding: "0 12px", borderRadius: 999, border: "1px solid #d9e2ea", display: "flex", alignItems: "center", fontSize: 14, textDecoration: "none" }}>{p.name}</Link>
                ))}
              </div>
            )}
          </form>
        </Panel>
      )}

      {drawer && (
        <div role="dialog" aria-modal="true" aria-label="Menu" style={{ position: "fixed", inset: 0, zIndex: 100, background: "#fff", display: "flex", flexDirection: "column" }}>
          <div style={{ height: 64, flexShrink: 0, display: "flex", alignItems: "center", gap: 8, padding: "0 12px 0 16px", borderBottom: "1px solid #e3eaf1" }}>
            {P ? (
              <>
                <Button variant="ghost" type="button" onClick={() => setProv(null)} aria-label="Back" style={{ ...iconBtn, marginLeft: -8, border: 0, background: "transparent" }}><Icon name="arrow_back" size={24} /></Button>
                <span style={{ fontSize: 17, fontWeight: 700 }}>{P.name}</span>
              </>
            ) : (
              <span style={{ fontSize: 17, fontWeight: 700 }}>Menu</span>
            )}
            <span style={{ flex: 1 }} />
            <Button variant="ghost" type="button" onClick={closeAll} aria-label="Close menu" style={iconBtn}><Icon name="close" size={24} /></Button>
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
            {P ? (
              <>
                <Link href={P.href} onClick={closeAll} style={{ minHeight: 52, display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 16, fontWeight: 600, textDecoration: "none", borderBottom: "1px solid #e3eaf1" }}>All lots in {P.name}<Icon name="arrow_forward" size={20} /></Link>
                {P.cities.map((c) => (
                  <Link key={c.name} href={c.href} onClick={closeAll} style={{ minHeight: 52, display: "flex", alignItems: "center", gap: 8, fontSize: 16, textDecoration: "none", borderBottom: "1px solid #e3eaf1" }}>
                    <span style={{ flex: 1 }}>{c.name}</span><span style={{ fontSize: 14, color: "#5E6776" }}>{c.count}</span><Icon name="chevron_right" size={20} color="#5E6776" />
                  </Link>
                ))}
              </>
            ) : (
              <>
                <form action={ROUTES.lots} style={{ display: "flex", alignItems: "center", gap: 10, height: 52, padding: "0 14px", border: "2px solid #121722", borderRadius: 12 }}>
                  <Icon name="search" />
                  <Input name="q" value={dq} onChange={(e) => setDq(e.target.value)} autoComplete="off" placeholder="Find a city, village or project" aria-label="Find a city, village or project" style={{ flex: 1, height: 44, border: 0, boxShadow: "none", outline: "none", font: "inherit", fontSize: 16, background: "transparent", minWidth: 0, padding: 0 }} />
                </form>
                {dRes.length > 0 && (
                  <div style={{ marginTop: 8, display: "flex", flexDirection: "column" }}>
                    {dRes.map((s) => (
                      <Link key={s.kind + s.label} href={s.href} onClick={closeAll} style={{ minHeight: 56, display: "flex", alignItems: "center", gap: 12, borderBottom: "1px solid #e3eaf1", textDecoration: "none" }}>
                        <Icon name={s.icon} color="#5E6776" />
                        <span style={{ flex: 1, minWidth: 0 }}><span style={{ display: "block", fontSize: 16, fontWeight: 600 }}>{s.label}</span><span style={{ display: "block", fontSize: 13, color: "#5E6776" }}>{s.kind}</span></span>
                        <span style={{ fontSize: 13, color: "#5E6776" }}>{s.meta}</span>
                      </Link>
                    ))}
                  </div>
                )}
                {!dq.trim() && (
                  <>
                    <a href={INQUIRE.sell} onClick={closeAll} style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 12, padding: 14, borderRadius: 14, background: "#EAF0FE", textDecoration: "none" }}>
                      <span style={{ width: 44, height: 44, borderRadius: 12, background: "#1A5CF0", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="add_home" size={24} color="#fff" /></span>
                      <span style={{ flex: 1 }}><span style={{ display: "block", fontSize: 17, fontWeight: 700 }}>List Your Lot</span><span style={{ display: "block", fontSize: 13, color: "#4A5262", marginTop: 2 }}>Sell with a licensed broker</span></span>
                      <Icon name="chevron_right" />
                    </a>
                    <div style={{ marginTop: 8 }}>
                      {drawerGroups.map((g) => (
                        <div key={g.id} style={{ borderBottom: "1px solid #e3eaf1" }}>
                          <Button variant="ghost" type="button" aria-expanded={acc === g.id} onClick={() => setAcc(acc === g.id ? null : g.id)} style={{ width: "100%", height: 56, border: 0, borderRadius: 0, background: "transparent", justifyContent: "space-between", font: "inherit", fontSize: 17, fontWeight: 600, color: "#121722", cursor: "pointer", padding: 0 }}>
                            {g.label}
                            <Icon name={acc === g.id ? "remove" : "add"} size={24} color="#5E6776" />
                          </Button>
                          {acc === g.id && (
                            <div style={{ padding: "0 0 12px", display: "flex", flexDirection: "column" }}>
                              {g.items.map((it) => (
                                <Link key={it.label} href={it.href} onClick={it.onClick ?? closeAll} style={{ minHeight: 48, display: "flex", alignItems: "center", gap: 12, fontSize: 16, textDecoration: "none" }}>
                                  <span style={{ width: 36, height: 36, borderRadius: 9, background: it.bg, display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name={it.icon} size={20} color={it.fg} /></span>
                                  <span style={{ flex: 1, minWidth: 0 }}>{it.label}</span>
                                  <span style={{ fontSize: 14, color: "#5E6776", fontVariantNumeric: "tabular-nums" }}>{it.meta}</span>
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                      <a href={INQUIRE.general} onClick={closeAll} style={{ height: 56, display: "flex", alignItems: "center", fontSize: 17, fontWeight: 600, textDecoration: "none", borderBottom: "1px solid #e3eaf1" }}>Contact</a>
                      <Link href={ROUTES.about} onClick={closeAll} style={{ height: 56, display: "flex", alignItems: "center", fontSize: 17, fontWeight: 600, textDecoration: "none", borderBottom: "1px solid #e3eaf1" }}>About</Link>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
          <div style={{ flexShrink: 0, padding: "12px 16px calc(12px + env(safe-area-inset-bottom))", borderTop: "1px solid #e3eaf1", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <a href={INQUIRE.general} onClick={closeAll} style={{ height: 50, borderRadius: 12, border: "1px solid #cfd9e3", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontSize: 16, fontWeight: 600, textDecoration: "none" }}><Icon name="forum" size={20} />Contact us</a>
            <a href={INQUIRE.sell} onClick={closeAll} className="lfs-btn-accent" style={{ height: 50, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontSize: 16, fontWeight: 600, textDecoration: "none" }}><Icon name="add_home" size={20} />List Your Lot</a>
          </div>
        </div>
      )}
    </header>
  )
}
